import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { CREDENTIAL_STATUS } from '@/data/credentialStatus';
import type { CredentialStatus } from '@/data/credentialStatus';

interface UseCredentialStatusResult {
  data: CredentialStatus | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Live credential status for the Insightful connector. Mirrors the
 * response shape of `GET /api/v1/orgs/:orgId/credentials/status` (see
 * web/src/routes/credentials.ts).
 *
 * The backend route is admin-only (task 027 / 050). Per parent Q9 the
 * callsite must already have gated the render path by role — this hook
 * deliberately does not enforce role itself (keep role logic in one
 * place, at the render site). When the render path hides the card, the
 * hook is never instantiated and no network call is made.
 */
export function useCredentialStatus(): UseCredentialStatusResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['credentialStatus', orgId],
    queryFn: async () => {
      const token = await getToken();
      return apiFetch<CredentialStatus>(`/api/v1/orgs/${orgId}/credentials/status`, token);
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) return { data: CREDENTIAL_STATUS, isLoading: false, error: null };
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
  };
}

// ── Mutations ───────────────────────────────────────────────────────────────
// Admin-only. The member-role gate already hides the card from the render
// tree (parent Q9), so these hooks are only instantiated from admin
// components. Mockup-mode callers should short-circuit at the component
// layer (see RotateCredentialModal / the Test button click path) — the
// mutations still work against a mockup-mode session but the network
// call will fail because currentOrg.id is a fixture value.

/**
 * Rotate (or first-time create) the Insightful credential for the
 * active org. Uses PUT /rotate when `exists` is true, POST otherwise.
 * Invalidates the credentialStatus query on success so the card
 * refreshes with the new lastRotatedAt.
 */
export function useRotateCredential() {
  const { currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();
  const orgId = currentOrg.id;

  return useMutation({
    mutationFn: async ({ value, exists }: { value: string; exists: boolean }) => {
      const token = await getToken();
      const url = exists
        ? `/api/v1/orgs/${orgId}/credentials/rotate`
        : `/api/v1/orgs/${orgId}/credentials`;
      return apiFetch<{ status: string }>(url, token, {
        method: exists ? 'PUT' : 'POST',
        body: JSON.stringify({ value }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['credentialStatus', orgId] });
    },
  });
}

/**
 * Delete the stored Insightful credential for the active org via
 * `DELETE /api/v1/orgs/:orgId/credentials` (task 109 D2). Admin-only.
 * Invalidates both the credentialStatus query and the collectors query
 * so the cards refresh with the new state immediately.
 *
 * Per ADR 0026 the backend does NOT archive the collector as a side
 * effect — the collector keeps its row but will stop syncing until a
 * new credential is set. The confirmation copy at the call site names
 * this plainly.
 */
export function useDeleteCredential() {
  const { currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();
  const orgId = currentOrg.id;

  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      // 204 returns no body; apiFetch handles the empty response. Typed
      // `unknown` here because the route returns no parseable body on
      // success, and the hook's consumers ignore the resolved value.
      await apiFetch<unknown>(`/api/v1/orgs/${orgId}/credentials`, token, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['credentialStatus', orgId] });
      void qc.invalidateQueries({ queryKey: ['collectors', orgId] });
    },
  });
}

export interface CredentialTestResponse {
  ok: boolean;
  reason?: string;
  checkedAt?: string;
}

/**
 * Ping the Insightful API with the stored credential via the backend's
 * POST /credentials/test endpoint. Returns the diagnostic outcome in the
 * body (`{ ok, reason? }`). A 404 surfaces as a thrown Error — the
 * caller shows a "no credential set" message from the component layer.
 */
export function useTestCredential() {
  const { currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();
  const orgId = currentOrg.id;

  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return apiFetch<CredentialTestResponse>(`/api/v1/orgs/${orgId}/credentials/test`, token, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['credentialStatus', orgId] });
    },
  });
}
